import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Button, Form, Badge, Container, InputGroup, Spinner } from 'react-bootstrap';
import { db, storage } from '../firebase';
import { ref as dbRef, onValue, push, update, remove, set } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';

const NoticeForm = ({ onSave, editing, initial, onCancel }) => {
  const [form, setForm] = useState(initial || { 
    title: '', 
    description: '', 
    category: 'Sports', 
    date: new Date().toISOString().split('T')[0],
    imageUrl: ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    setForm(initial || { 
      title: '', 
      description: '', 
      category: 'Sports', 
      date: new Date().toISOString().split('T')[0],
      imageUrl: ''
    });
    setImageFile(null);
  }, [initial]);

  const handleImageChange = (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      setImageFile(file);
      // Create preview URL
      setForm(prev => ({ ...prev, imageUrl: URL.createObjectURL(file) }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;

    let imageUrl = form.imageUrl;
    
    if (imageFile) {
      setIsUploading(true);
      try {
        const imageName = uuidv4();
        const storageReference = storageRef(storage, `notice-images/${imageName}`);
        await uploadBytes(storageReference, imageFile);
        imageUrl = await getDownloadURL(storageReference);
      } catch (error) {
        console.error("Image upload error:", error);
        alert('Image upload failed');
        setIsUploading(false);
        return;
      }
      setIsUploading(false);
    }

    onSave({
      ...form,
      imageUrl
    });

    if (!editing) {
      setForm({ 
        title: '', 
        description: '', 
        category: 'Sports', 
        date: new Date().toISOString().split('T')[0],
        imageUrl: ''
      });
      setImageFile(null);
    }
  };

  const removeImage = () => {
    if (form.imageUrl.startsWith('blob:')) {
      URL.revokeObjectURL(form.imageUrl);
    }
    setForm(prev => ({ ...prev, imageUrl: '' }));
    setImageFile(null);
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Row className="g-3">
        <Col md={12}>
          <Form.Control
            placeholder="Title *"
            value={form.title}
            onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            required
            className="mb-2"
          />
        </Col>
        <Col md={12}>
          <Form.Control
            as="textarea"
            rows={3}
            placeholder="Description *"
            value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            required
            className="mb-2"
          />
        </Col>
        <Col md={6}>
          <Form.Select
            value={form.category}
            onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
            className="mb-2"
          >
            <option value="Sports">Sports</option>
            <option value="Academics">Academics</option>
            <option value="Cultural">Cultural</option>
            <option value="General">General</option>
          </Form.Select>
        </Col>
        <Col md={6}>
          <Form.Control
            type="date"
            value={form.date}
            onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
            className="mb-2"
          />
        </Col>
        <Col md={12}>
          <Form.Label>Notice Image</Form.Label>
          {form.imageUrl ? (
            <div className="mb-3">
              <img 
                src={form.imageUrl} 
                alt="Preview" 
                style={{ maxHeight: '150px', marginBottom: '10px' }} 
                className="img-fluid rounded"
              />
              <Button 
                variant="outline-danger" 
                size="sm" 
                onClick={removeImage}
                className="ms-2"
              >
                Remove Image
              </Button>
            </div>
          ) : (
            <Form.Control
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="mb-2"
            />
          )}
          {isUploading && (
            <div className="mt-2">
              <Spinner animation="border" size="sm" className="me-2" />
              Uploading image...
            </div>
          )}
        </Col>
      </Row>
      <div className="mt-3 d-flex gap-2">
        <Button 
          type="submit" 
          variant="primary" 
          className="px-4"
          disabled={isUploading}
        >
          {editing ? 'Update Notice' : 'Add Notice'}
        </Button>
        {editing && (
          <Button variant="outline-secondary" onClick={onCancel}>
            Cancel
          </Button>
        )}
      </div>
    </Form>
  );
};

const NoticeCard = ({ notice, onEdit, onDelete }) => {
  const categoryColors = {
    Sports: 'primary',
    Academics: 'success',
    Cultural: 'warning',
    General: 'info'
  };

  return (
    <Card className="h-100 shadow-sm border-0">
      {notice.imageUrl && (
        <Card.Img 
          variant="top" 
          src={notice.imageUrl} 
          style={{ height: '180px', objectFit: 'cover' }} 
        />
      )}
      <Card.Body className="d-flex flex-column">
        <div className="d-flex justify-content-between align-items-start mb-2">
          <Badge bg={categoryColors[notice.category]} className="mb-2">
            {notice.category}
          </Badge>
          <small className="text-muted">{notice.date}</small>
        </div>
        <Card.Title className="mb-3">{notice.title}</Card.Title>
        <Card.Text className="flex-grow-1">{notice.description}</Card.Text>
        <div className="d-flex gap-2 mt-3">
          <Button 
            variant="outline-primary" 
            size="sm" 
            onClick={() => onEdit(notice)}
          >
            Edit
          </Button>
          <Button 
            variant="outline-danger" 
            size="sm" 
            onClick={() => onDelete(notice)}
          >
            Delete
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
};

const NoticesBoard = () => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('All');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const noticesRef = dbRef(db, 'notices');
    setLoading(true);

    const unsubscribe = onValue(noticesRef, (snap) => {
      const data = snap.val();
      if (data) {
        const arr = Object.entries(data).map(([id, v]) => ({ id, ...v }));
        arr.sort((a, b) => new Date(b.date) - new Date(a.date));
        setItems(arr);
      } else {
        setItems([]);
      }
      setLoading(false);
    }, (error) => {
      console.error("Data fetch error:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleSave = async (form) => {
    const payload = {
      title: form.title.trim(),
      description: form.description.trim(),
      category: form.category,
      date: form.date || new Date().toISOString().split('T')[0],
      imageUrl: form.imageUrl || '',
      timestamp: Date.now(),
    };

    try {
      if (form.id) {
        // Delete old image if it's being replaced
        if (editing?.imageUrl && form.imageUrl !== editing.imageUrl) {
          try {
            const oldImageRef = storageRef(storage, editing.imageUrl);
            await deleteObject(oldImageRef);
          } catch (error) {
            console.error("Old image delete error:", error);
          }
        }
        await update(dbRef(db, `notices/${form.id}`), payload);
      } else {
        const newNoticeRef = push(dbRef(db, 'notices'));
        await set(newNoticeRef, payload);
      }
      setEditing(null);
    } catch (error) {
      alert(`Error: ${error.message}`);
      console.error("Save error:", error);
    }
  };

  const handleDelete = async (notice) => {
    if (window.confirm('Delete this notice permanently?')) {
      try {
        // Only try to delete from storage if the imageUrl is a Firebase Storage URL
        if (notice.imageUrl && notice.imageUrl.startsWith('https://')) {
          try {
            // Extract the storage path from the URL
            const url = new URL(notice.imageUrl);
            const pathMatch = url.pathname.match(/\/o\/(.+)\?/);
            if (pathMatch && pathMatch[1]) {
              const decodedPath = decodeURIComponent(pathMatch[1]);
              await deleteObject(storageRef(storage, decodedPath));
            }
          } catch (error) {
            // Log but don't block deletion if image delete fails
            console.error("Image delete error:", error);
          }
        }
        await remove(dbRef(db, `notices/${notice.id}`));
      } catch (error) {
        alert(`Delete failed: ${error.message}`);
        console.error("Delete error:", error);
      }
    }
  };

  const filteredNotices = items.filter(notice =>
    (filter === 'All' || notice.category === filter) &&
    (notice.title.toLowerCase().includes(search.toLowerCase()) || 
     notice.description.toLowerCase().includes(search.toLowerCase()))
  );

  // Get latest events and news
  const latestEvents = items.filter(i => i.category === 'Cultural').slice(0, 3);
  const latestNews = items.filter(i => i.category === 'General').slice(0, 3);

  const handleEdit = (notice) => setEditing(notice);

  return (
    <Container className="py-4">
      {/* Campus Events and Latest News Section */}
      <Row className="g-4 mb-5">
        <Col md={6}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="fw-bold m-0 text-primary">Campus Events</h4>
                <Badge bg="primary" pill>{latestEvents.length}</Badge>
              </div>
              {latestEvents.length > 0 ? (
                <div className="list-group list-group-flush">
                  {latestEvents.map(event => (
                    <div key={event.id} className="list-group-item border-0 px-0 py-3">
                      {event.imageUrl && (
                        <img src={event.imageUrl} alt={event.title} className="img-fluid rounded mb-2" style={{ maxHeight: '150px', width: '100%', objectFit: 'cover' }} />
                      )}
                      <h5 className="mb-1">{event.title}</h5>
                      <p className="mb-1 text-muted">{event.description}</p>
                      <div className="d-flex justify-content-between align-items-center mt-2">
                        <small className="text-primary">{event.date}</small>
                        <Badge bg="info">Event</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-3 text-muted">No upcoming events</div>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="fw-bold m-0 text-success">Latest News</h4>
                <Badge bg="success" pill>{latestNews.length}</Badge>
              </div>
              {latestNews.length > 0 ? (
                <div className="list-group list-group-flush">
                  {latestNews.map(news => (
                    <div key={news.id} className="list-group-item border-0 px-0 py-3">
                      {news.imageUrl && (
                        <img src={news.imageUrl} alt={news.title} className="img-fluid rounded mb-2" style={{ maxHeight: '150px', width: '100%', objectFit: 'cover' }} />
                      )}
                      <h5 className="mb-1">{news.title}</h5>
                      <p className="mb-1 text-muted">{news.description}</p>
                      <div className="d-flex justify-content-between align-items-center mt-2">
                        <small className="text-success">{news.date}</small>
                        <Badge bg="success">News</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-3 text-muted">No latest news</div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="fw-bold text-primary m-0">Notices Board</h2>
        <Badge bg="light" text="dark" className="fs-6">
          {items.length} {items.length === 1 ? 'Notice' : 'Notices'}
        </Badge>
      </div>

      <Card className="mb-4 border-0 shadow-sm">
        <Card.Body>
          <h5 className="mb-3">
            {editing ? 'Edit Notice' : 'Add New Notice'}
          </h5>
          <NoticeForm
            onSave={handleSave}
            editing={!!editing}
            initial={editing}
            onCancel={() => setEditing(null)}
          />
        </Card.Body>
      </Card>

      <Row className="mb-4 g-3 align-items-center">
        <Col md={4}>
          <Form.Select 
            value={filter} 
            onChange={e => setFilter(e.target.value)}
            className="shadow-sm"
          >
            <option value="All">All Categories</option>
            <option value="Sports">Sports</option>
            <option value="Academics">Academics</option>
            <option value="Cultural">Cultural</option>
            <option value="General">General</option>
          </Form.Select>
        </Col>
        <Col md={8}>
          <InputGroup className="shadow-sm">
            <Form.Control
              placeholder="Search notices..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <Button variant="outline-secondary">
              <i className="bi bi-search"></i>
            </Button>
          </InputGroup>
        </Col>
      </Row>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" className="text-primary" />
          <p className="mt-2">Loading notices...</p>
        </div>
      ) : (
        <Row xs={1} md={2} lg={3} className="g-4">
          {filteredNotices.map(notice => (
            <Col key={notice.id}>
              <NoticeCard 
                notice={notice} 
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            </Col>
          ))}
        </Row>
      )}
    </Container>
  );
};

export default NoticesBoard;